import React from 'react';
import { useInRouterContext, useNavigate } from 'react-router';

/**
 * Returns a replaceUrl(path) function that replaces the current URL without adding a history entry.
 *
 * Inside a router, replaceUrl goes through navigate(), so react-router's location stays in sync with the address bar.
 * window.history.replaceState changes the URL without updating react-router's location.
 *
 * Outside a router, replaceUrl falls back to window.history.replaceState.
 */
export function useReplaceUrl(): (path: string) => void {
  const inRouterContext = useInRouterContext();
  // useNavigate throws outside a router. A given component is either always
  // inside a router or never, so this conditional never flips between renders.
  // See Redirect in routing_result.tsx for the same pattern.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const navigate = inRouterContext ? useNavigate() : undefined;

  return React.useCallback(
    function replaceUrl(path: string) {
      if (navigate) {
        navigate(path, { replace: true, state: { silentNavigation: true } });
      } else {
        window.history.replaceState(window.history.state, '', path);
      }
    },
    [navigate],
  );
}
